import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { Target, Plus, Trash2, Edit2, Search, Download, Play, Pause, CheckSquare, Square } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { Pagination } from '../components/Pagination';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionsBar } from '../components/BulkActionsBar';
import { exportToCSV, ExportColumn, formatDateForExport } from '../utils/exportData';

interface Campaign {
  id: string;
  name: string;
  description: string;
  campaign_type: 'workflow' | 'one_time_email' | 'one_time_sms' | 'multi_channel';
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'cancelled';
  company_id: string;
  segment_id: string | null;
  workflow_id: string | null;
  target_count: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  converted_count: number;
  created_at: string;
  updated_at: string;
}

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadCampaigns();
    
    const channel = supabase
      .channel('campaigns-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'marketing_campaigns' }, () => {
        loadCampaigns();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const loadCampaigns = async () => {
    try {
      const { data, error } = await supabase
        .from('marketing_campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this campaign?')) return;
    try {
      const { error } = await supabase.from('marketing_campaigns').delete().eq('id', id);
      if (error) throw error;
      toast.success('Campaign deleted');
      loadCampaigns();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handleStatusChange = async (campaign: Campaign, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('marketing_campaigns')
        .update({ status: newStatus })
        .eq('id', campaign.id);
      if (error) throw error;
      toast.success(`Campaign ${newStatus}`);
      loadCampaigns();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update status');
    }
  };

  const filteredCampaigns = campaigns.filter(c =>
    c.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCampaigns = filteredCampaigns.slice(startIndex, startIndex + itemsPerPage);
  const bulkSelection = useBulkSelection(paginatedCampaigns, (c) => c.id);

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${bulkSelection.selectedCount} campaign(s)?`)) return;
    try {
      const { error } = await supabase
        .from('marketing_campaigns')
        .delete()
        .in('id', bulkSelection.selectedIds);
      if (error) throw error;
      toast.success(`${bulkSelection.selectedCount} campaign(s) deleted`);
      bulkSelection.deselectAll();
      loadCampaigns();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete campaigns');
    }
  };

  const handleExport = () => {
    const columns: ExportColumn<Campaign>[] = [
      { key: 'name', header: 'Name' },
      { key: 'campaign_type', header: 'Type' },
      { key: 'status', header: 'Status' },
      { key: 'target_count', header: 'Target' },
      { key: 'sent_count', header: 'Sent' },
      { key: 'delivered_count', header: 'Delivered' },
      { key: 'opened_count', header: 'Opened' },
      { key: 'clicked_count', header: 'Clicked' },
      { key: 'converted_count', header: 'Converted' },
      { key: 'created_at', header: 'Created', format: formatDateForExport },
    ];
    exportToCSV(filteredCampaigns, columns, `campaigns-${new Date().toISOString().split('T')[0]}.csv`);
    toast.success(`Exported ${filteredCampaigns.length} campaign(s)`);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Campaign Management</h1>
          <p className="text-gray-600 mt-2">Loading campaigns...</p>
        </div>
        <LoadingSkeleton type="stats" count={4} />
        <LoadingSkeleton type="list" count={3} />
      </div>
    );
  }

  const statuses =  ['draft', 'scheduled', 'active', 'completed', 'cancelled'];
  const statusCounts = statuses.map(status => ({
    status,
    count: campaigns.filter(c => c.status === status).length
  }));

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Campaign Management</h1>
            <p className="text-gray-600 mt-2">Create and manage marketing campaigns</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleExport} disabled={filteredCampaigns.length === 0}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2">
              <Download size={18} /> Export CSV
            </button>
            <button onClick={() => { setEditingCampaign(null); setShowModal(true); }}
              className="btn-primary flex items-center gap-2">
              <Plus size={20} /> Create Campaign
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input type="text" placeholder="Search campaigns..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className="input pl-10 w-full max-w-md" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        {statusCounts.map(({ status, count }) => (
          <div key={status} className="card">
            <p className="text-sm font-medium text-gray-600 capitalize">{status}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
          </div>
        ))}
      </div>

      <BulkActionsBar
        selectedCount={bulkSelection.selectedCount}
        onDeselectAll={bulkSelection.deselectAll}
        onBulkDelete={handleBulkDelete}
      />

      {paginatedCampaigns.length === 0 ? (
        <div className="card text-center py-12">
          <Target className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm ? 'No campaigns found' : 'No campaigns yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm ? 'Try adjusting your search' : 'Create your first campaign to start'}
          </p>
          {!searchTerm && (
            <button onClick={() => setShowModal(true)} className="btn-primary">
              <Plus size={20} className="inline mr-2" /> Create Campaign
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {paginatedCampaigns.map((campaign) => (
            <div key={campaign.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <button onClick={() => bulkSelection.toggleSelect(campaign.id)}
                    className="p-1 hover:bg-gray-100 rounded mt-1">
                    {bulkSelection.isSelected(campaign.id) ?
                      <CheckSquare className="text-blue-600" size={20} /> :
                      <Square className="text-gray-400" size={20} />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                        campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        campaign.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {campaign.status}
                      </span>
                      <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">
                        {campaign.campaign_type.replace(/_/g, ' ')}
                      </span>
                    </div>
                    {campaign.description && <p className="text-gray-600 mb-3">{campaign.description}</p>}
                    <div className="grid grid-cols-5 gap-4 text-sm">
                      <div><span className="text-gray-500">Target:</span> <span className="font-semibold">{campaign.target_count}</span></div>
                      <div><span className="text-gray-500">Sent:</span> <span className="font-semibold">{campaign.sent_count}</span></div>
                      <div><span className="text-gray-500">Delivered:</span> <span className="font-semibold">{campaign.delivered_count}</span></div>
                      <div><span className="text-gray-500">Opened:</span> <span className="font-semibold">{campaign.opened_count}</span></div>
                      <div><span className="text-gray-500">Clicked:</span> <span className="font-semibold">{campaign.clicked_count}</span></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {campaign.status === 'draft' && (
                    <button onClick={() => handleStatusChange(campaign, 'active')}
                      className="px-3 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded text-sm font-medium">
                      <Play size={14} className="inline mr-1" /> Launch
                    </button>
                  )}
                  {campaign.status === 'active' && (
                    <button onClick={() => handleStatusChange(campaign, 'cancelled')}
                      className="px-3 py-1 bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded text-sm font-medium">
                      <Pause size={14} className="inline mr-1" /> Pause
                    </button>
                  )}
                  <button onClick={() => { setEditingCampaign(campaign); setShowModal(true); }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDelete(campaign.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredCampaigns.length > 0 && (
        <div className="mt-6 card">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredCampaigns.length}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
          />
        </div>
      )}

      {showModal && (
        <CampaignModal
          campaign={editingCampaign}
          onClose={() => { setShowModal(false); setEditingCampaign(null); }}
          onSave={() => { setShowModal(false); setEditingCampaign(null); loadCampaigns(); }}
        />
      )}
    </div>
  );
}

function CampaignModal({ campaign, onClose, onSave }: {
  campaign: Campaign | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const [name, setName] = useState(campaign?.name || '');
  const [description, setDescription] = useState(campaign?.description || '');
  const [campaignType, setCampaignType] = useState(campaign?.campaign_type || 'one_time_email');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim(),
        campaign_type: campaignType,
        company_id: 'halesac',
        status: campaign?.status || 'draft',
      };

      if (campaign) {
        const { error } = await supabase.from('marketing_campaigns').update(data).eq('id', campaign.id);
        if (error) throw error;
        toast.success('Campaign updated');
      } else {
        const { error } = await supabase.from('marketing_campaigns').insert(data);
        if (error) throw error;
        toast.success('Campaign created');
      }
      onSave();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {campaign ? 'Edit Campaign' : 'Create Campaign'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Summer Promotion 2024" className="input w-full" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this campaign..." rows={3} className="input w-full" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Type *</label>
              <select value={campaignType} onChange={(e) => setCampaignType(e.target.value as any)}
                className="input w-full">
                <option value="one_time_email">One-Time Email</option>
                <option value="one_time_sms">One-Time SMS</option>
                <option value="workflow">Workflow</option>
                <option value="multi_channel">Multi-Channel</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t mt-6">
            <button onClick={onClose} disabled={saving}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : campaign ? 'Update Campaign' : 'Create Campaign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
